// pages/Invoices.js
// Unified invoices page showing all invoice types: Event Tier (EVT), Storage (SS), Album Extension (EXT)

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiFileText,
  FiSearch,
  FiDownload,
  FiEye,
  FiX,
  FiCalendar,
  FiDollarSign,
  FiPercent,
  FiHardDrive,
  FiTrendingUp,
  FiImage,
  FiClock,
  FiArrowLeft
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';

// Import invoice services
import {
  getUserEventTierInvoices,
} from '../services/eventUpgradeInvoiceService';
import { formatMoney } from '../shared/upgradePricing';
import {
  generateEventTierInvoiceHTML,
} from '../services/eventUpgradeInvoiceTemplates';

import {
  getUserStorageInvoices,
} from '../services/storageInvoiceService';
import { generateStorageInvoiceHTML } from '../services/storageInvoiceTemplates';

import {
  getUserAlbumExtensionInvoices,
} from '../services/albumExtensionInvoiceService';
import { generateAlbumExtensionInvoiceHTML } from '../services/albumExtensionInvoiceTemplates';

import {
  getUserPhotoUploadInvoices,
} from '../services/photoUploadInvoiceService';
import { generatePhotoUploadInvoiceHTML } from '../services/photoUploadInvoiceTemplates';

import '../styles/Invoices.scss';

// Invoice type configuration
const INVOICE_TYPES = {
  all: { label: 'All Invoices', icon: FiFileText, color: '#7C4DFF' },
  event: { label: 'Event Upgrades', icon: FiTrendingUp, color: '#FF6B6B', prefix: 'EVT' },
  storage: { label: 'Storage', icon: FiHardDrive, color: '#4ECDC4', prefix: 'SS' },
  album: { label: 'Album Extension', icon: FiImage, color: '#FFD93D', prefix: 'EXT' },
  photoUpload: { label: 'Photo Upload', icon: FiClock, color: '#8B5CF6', prefixes: ['PUEX', 'PUUP'] }
};

const currencyOf = (invoice) => invoice?.currency || 'INR';

/** Totals kept per currency - rupees and dollars do not add up. */
const formatTotals = (byCurrency) => {
  const entries = Object.entries(byCurrency).filter(([, amount]) => amount > 0);
  if (entries.length === 0) return formatMoney(0, 'INR');
  return entries
    .sort(([a], [b]) => (a === 'INR' ? -1 : b === 'INR' ? 1 : a.localeCompare(b)))
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(' + ');
};

// Invoice Viewer Modal
const InvoiceViewerModal = ({ isOpen, onClose, invoiceHtml, invoiceNumber, title }) => {
  if (!isOpen) return null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(invoiceHtml);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([invoiceHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Invoice-${invoiceNumber}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="invoice-viewer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="invoice-viewer-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="viewer-header">
            <div className="viewer-title">
              <FiFileText />
              <span>{title || 'Invoice'}</span>
              <span className="invoice-number">{invoiceNumber}</span>
            </div>
            <div className="viewer-actions">
              <button className="action-btn" onClick={handlePrint} title="Print">
                <FiFileText />
                <span>Print</span>
              </button>
              <button className="action-btn" onClick={handleDownload} title="Download">
                <FiDownload />
                <span>Download</span>
              </button>
              <button className="close-btn" onClick={onClose}>
                <FiX />
              </button>
            </div>
          </div>
          <div className="viewer-content">
            <iframe
              srcDoc={invoiceHtml}
              title="Invoice Preview"
              className="invoice-iframe"
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue, color }) => (
  <motion.div
    className="stats-card"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="stats-icon" style={{ backgroundColor: `${color}20`, color }}>
      <Icon size={20} />
    </div>
    <div className="stats-info">
      <span className="stats-value">{value}</span>
      <span className="stats-label">{label}</span>
      {subValue && <span className="stats-sub">{subValue}</span>}
    </div>
  </motion.div>
);

// Invoice Row Component
const InvoiceRow = ({ invoice, type, onView }) => {
  const typeConfig = INVOICE_TYPES[type] || INVOICE_TYPES.event;
  const Icon = typeConfig.icon;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDescription = () => {
    switch (type) {
      case 'event':
        return invoice.event?.title || invoice.event_title || 'Event Upgrade';
      case 'storage':
        return invoice.studio?.name || 'Storage Purchase';
      case 'album':
        return invoice.album?.name || invoice.album_name || 'Album Extension';
      case 'photoUpload':
        return `${invoice.event_title || 'Event'} - Photo Upload ${invoice.purchase_type === 'extension' ? 'Extension' : 'Upgrade'}`;
      default:
        return 'Invoice';
    }
  };

  return (
    <motion.tr
      className="invoice-row"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
    >
      <td className="invoice-number-cell">
        <div className="invoice-badge" style={{ borderColor: typeConfig.color }}>
          <Icon size={14} style={{ color: typeConfig.color }} />
          <span>{invoice.invoice_number}</span>
        </div>
      </td>
      <td className="date-cell">
        <FiCalendar size={14} />
        <span>{formatDate(invoice.generated_at)}</span>
      </td>
      <td className="type-cell">
        <span className="type-badge" style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}>
          {typeConfig.label}
        </span>
      </td>
      <td className="description-cell">
        <span className="description">{getDescription()}</span>
      </td>
      <td className="amount-cell">
        <div className="amount-info">
          <span className="total">{formatMoney(parseFloat(invoice.total_amount), currencyOf(invoice))}</span>
          <span className="gst">GST: {formatMoney(parseFloat(invoice.total_gst), currencyOf(invoice))}</span>
        </div>
      </td>
      <td className="actions-cell">
        <button className="view-btn" onClick={() => onView(invoice, type)}>
          <FiEye size={16} />
          <span>View</span>
        </button>
      </td>
    </motion.tr>
  );
};

const Invoices = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allInvoices, setAllInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalSpent: {},
    totalGst: {}
  });

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterFY, setFilterFY] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Invoice viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerHtml, setViewerHtml] = useState('');
  const [viewerInvoice, setViewerInvoice] = useState(null);
  const [viewerType, setViewerType] = useState('');

  // Get unique financial years
  const financialYears = [...new Set(allInvoices.map(inv => inv.financial_year))].sort().reverse();

  useEffect(() => {
    loadAllInvoices();
  }, []);

  const loadAllInvoices = async () => {
    try {
      setLoading(true);

      // Load all invoice types in parallel
      const [eventResult, storageResult, albumResult, photoUploadResult] = await Promise.all([
        getUserEventTierInvoices(),
        getUserStorageInvoices(),
        getUserAlbumExtensionInvoices(),
        getUserPhotoUploadInvoices()
      ]);

      // Combine and tag invoices with their type
      const combined = [];
      const totalSpent = {};
      const totalGst = {};
      const add = (inv) => {
        const c = currencyOf(inv);
        totalSpent[c] = (totalSpent[c] || 0) + (parseFloat(inv.total_amount) || 0);
        totalGst[c] = (totalGst[c] || 0) + (parseFloat(inv.total_gst) || 0);
      };

      if (eventResult.success && eventResult.invoices) {
        eventResult.invoices.forEach(inv => {
          combined.push({ ...inv, _type: 'event' });
          add(inv);
        });
      }

      if (storageResult.success && storageResult.invoices) {
        storageResult.invoices.forEach(inv => {
          combined.push({ ...inv, _type: 'storage' });
          add(inv);
        });
      }

      if (albumResult.success && albumResult.invoices) {
        albumResult.invoices.forEach(inv => {
          combined.push({ ...inv, _type: 'album' });
          add(inv);
        });
      }

      if (photoUploadResult.success && photoUploadResult.invoices) {
        photoUploadResult.invoices.forEach(inv => {
          combined.push({ ...inv, _type: 'photoUpload' });
          add(inv);
        });
      }

      // Sort by generated_at descending
      combined.sort((a, b) => new Date(b.generated_at) - new Date(a.generated_at));

      setAllInvoices(combined);
      setStats({
        totalInvoices: combined.length,
        totalSpent,
        totalGst
      });

    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter invoices
  const filteredInvoices = allInvoices.filter(inv => {
    // Type filter
    if (filterType !== 'all' && inv._type !== filterType) return false;

    // Financial year filter
    if (filterFY !== 'all' && inv.financial_year !== filterFY) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchNumber = inv.invoice_number?.toLowerCase().includes(query);
      const matchDesc = (inv.event?.title || inv.album?.name || inv.studio?.name || '')
        .toLowerCase().includes(query);
      if (!matchNumber && !matchDesc) return false;
    }

    return true;
  });

  // View invoice
  const handleViewInvoice = (invoice, type) => {
    let html = '';
    let title = '';

    switch (type) {
      case 'event':
        html = generateEventTierInvoiceHTML(invoice);
        title = 'Event Upgrade Invoice';
        break;
      case 'storage':
        html = generateStorageInvoiceHTML(invoice);
        title = 'Storage Invoice';
        break;
      case 'album':
        html = generateAlbumExtensionInvoiceHTML(invoice);
        title = 'Album Extension Invoice';
        break;
      case 'photoUpload':
        html = generatePhotoUploadInvoiceHTML(invoice);
        title = `Photo Upload ${invoice.purchase_type === 'extension' ? 'Extension' : 'Upgrade'} Invoice`;
        break;
      default:
        return;
    }

    setViewerHtml(html);
    setViewerInvoice(invoice);
    setViewerType(title);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerHtml('');
    setViewerInvoice(null);
  };

  return (
    <DashboardLayout>
      <div className="invoices-page">
        <div className="invoices-container">
          {/* Header */}
          <motion.div
            className="invoices-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button className="back-btn" onClick={() => navigate(-1)}>
              <FiArrowLeft size={22} />
            </button>
            <div className="header-content">
              <h1>Invoices</h1>
              <p>View and download all your invoices in one place</p>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StatsCard
              icon={FiFileText}
              label="Total Invoices"
              value={stats.totalInvoices}
              color="#7C4DFF"
            />
            <StatsCard
              icon={FiDollarSign}
              label="Total Spent"
              value={formatTotals(stats.totalSpent)}
              color="#4ECDC4"
            />
            <StatsCard
              icon={FiPercent}
              label="GST Paid"
              value={formatTotals(stats.totalGst)}
              color="#FFD93D"
            />
          </motion.div>

          {/* Filters */}
          <motion.div
            className="filters-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by invoice number or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-btn" onClick={() => setSearchQuery('')}>
                  <FiX />
                </button>
              )}
            </div>

            <div className="filter-group">
              <div className="filter-item">
                <label>Type</label>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  {Object.entries(INVOICE_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Financial Year</label>
                <select value={filterFY} onChange={(e) => setFilterFY(e.target.value)}>
                  <option value="all">All Years</option>
                  {financialYears.map(fy => (
                    <option key={fy} value={fy}>FY 20{fy}-{parseInt(fy) + 1}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="results-count">
              Showing {filteredInvoices.length} of {allInvoices.length} invoices
            </div>
          </motion.div>

          {/* Invoices Table */}
          <motion.div
            className="invoices-table-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading invoices...</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="empty-state">
                <FiFileText size={48} />
                <h3>No Invoices Found</h3>
                <p>
                  {allInvoices.length === 0
                    ? 'Your invoices will appear here after you make a purchase.'
                    : 'No invoices match your current filters.'}
                </p>
              </div>
            ) : (
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice, index) => (
                    <InvoiceRow
                      key={`${invoice._type}-${invoice.id}`}
                      invoice={invoice}
                      type={invoice._type}
                      onView={handleViewInvoice}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </motion.div>

          {/* GST Info */}
          <motion.div
            className="gst-info-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="info-icon">
              <FiPercent />
            </div>
            <div className="info-content">
              <h4>About GST on Services</h4>
              <p>
                All services are charged 18% GST. Event upgrades use HSN/SAC Code 998314 (Online content services),
                while storage and album extension services use HSN/SAC Code 998315 (Hosting and IT infrastructure provisioning services).
                GST is calculated based on your state.
                These invoices can be used for tax credit if applicable.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Invoice Viewer Modal */}
        <InvoiceViewerModal
          isOpen={viewerOpen}
          onClose={handleCloseViewer}
          invoiceHtml={viewerHtml}
          invoiceNumber={viewerInvoice?.invoice_number || ''}
          title={viewerType}
        />
      </div>
    </DashboardLayout>
  );
};

export default Invoices;
